import { ChevronRight } from 'lucide-react';
import { dropsSpaceCrumb, folderTitle, joinPath, pathSegments } from '../../../lib/file-browser';
import { useTranslation } from '../../../lib/i18n';
import { TextButton } from '../ui';
import styles from './FilesFolderNav.module.css';

// The BREADCRUMB: where you are, and every step back. The folder ROWS live in `FilesTable`, on the same column
// skeleton as the file rows — a folder and a file are read as one list, so they cannot be laid out separately.
//
// TYPE, off TWO Figma nodes — `15623:362406` (the wide control band) and `15891:421734` (the phone's
// breadcrumb line). They agree on almost everything: crumbs 4 apart, a non-terminal crumb 14/**20**
// **Regular** in `text&icon/secondary` and never wrapping, a 16px chevron-right separator that mirrors
// under RTL, a terminal crumb 14/**18** in the primary ink.
//
// ⚠️ THEY DISAGREE ON THE TERMINAL CRUMB'S WEIGHT: the wide node draws it **SemiBold**, the phone node
// **Medium**. Both were checked. Each surface is implemented as its own node draws it (see
// `.crumbCurrent` in the stylesheet) rather than one being declared the winner — the same call
// `FolderGlyph` makes about the folder mark's two tones, and for the same reason: a design that says two
// different things in two places is a fact about the design, and burying it makes the next person
// "fix" it back.
//
// The two navigable crumbs are DS `TextButton`s, not `<Button variant="text">`: Figma's crumb is a word,
// not a quiet button, and the Ghost button's 30px box and 12px inline padding were what pushed each crumb
// 24px wider than its own text and gave it a hover fill the design never draws. The terminal crumb is a
// plain `<span>` carrying the type as a module class — it is the one bespoke role on this line, and a
// `Typography` variant class would have to be out-specified rather than read.
//
// There is no "create folder" and no drag-and-drop. A folder here is not a stored thing — it exists precisely
// because a file's path has that segment in it (see `lib/file-browser.ts`), so an empty one cannot be
// represented at all. A file moves folders through "Move to…", which is a real write to a real path.

export function FilesBreadcrumb({
  folder,
  root,
  onNavigate,
  workspaceNames,
  hideRootWorkspace = false,
  showRootWhenEmpty = false,
  rootLabel,
  className,
}: {
  folder: string;
  /** Where this browser starts — `''` for the cross-workspace page, the workspace id for its own gallery. */
  root: string;
  onNavigate: (folder: string) => void;
  workspaceNames?: Record<string, string>;
  /**
   * Drop the FIRST segment — the Space — from the trail, leaving `All Files › Folder [› Subfolder…]`.
   *
   * Only meaningful on the merged tree (`root === ''`), where segment 0 is a workspace id. The Space step
   * was a crumb that named a level the reader had not chosen and could not usefully go back to, and the
   * Space is already said, at every level, by the Workspace COLUMN (list) and the space badge (grid) —
   * see `FilesBrowser`'s `mergedRoot`. The intermediate crumbs still carry the full path, so navigating
   * into and back out of a folder lands exactly where it did before.
   *
   * The "Move to…" dialog passes it FALSE on purpose: there, picking the Space IS the first step.
   */
  hideRootWorkspace?: boolean;
  /**
   * At the ROOT — where there are no segments below it — say so, instead of rendering nothing.
   *
   * The trail used to disappear entirely at a root, which left the control band's leading edge empty
   * and let the chips drift into the space where the crumb belongs. The Figma draws "All Files" sitting
   * there, and a band whose leading edge is sometimes a label and sometimes nothing reads as two
   * different screens.
   *
   * The root crumb is then the CURRENT page, so it is the same `<Typography aria-current="page">` the
   * last step of a deeper trail is — not a Button. You are already there; a control that navigates to
   * where you already are is a promise the screen cannot keep.
   *
   * `FolderPickerDialog` deliberately does NOT pass it: its first level really is a row of Spaces to
   * pick from, so there the empty trail is the truth.
   */
  showRootWhenEmpty?: boolean;
  /**
   * What the FIRST crumb is called. Defaults to "All files", which is the truth on the surfaces that
   * really are rooted at the top of the path space — and a lie on any surface that is not.
   *
   * An agent's page browses `brain/<agent>`, and reading "All files" there told the reader they were
   * looking at every file in the account when they were looking at one agent's folder. A rooted surface
   * names its root. Reported.
   */
  rootLabel?: string;
  /** Placement from the enclosing surface — the crumb owns its steps, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();
  // Only the segments BELOW the root are steps of ours; the root itself is the leading crumb.
  const rootSegments = pathSegments(root);
  const all = pathSegments(folder).slice(rootSegments.length);
  // Rendered steps only — the PATHS below are still computed from `all`, so a hidden step is hidden, not
  // removed from the tree.
  // An ACCOUNT root is never dropped, whatever the caller asked for — see `dropsSpaceCrumb`, which owns
  // the rule because it is a rule about the PATH and both phones mirror it.
  const shown = dropsSpaceCrumb(hideRootWorkspace, all) ? all.slice(1) : all;
  const atRoot = shown.length === 0;
  if (atRoot && !showRootWhenEmpty) return null;
  const skipped = all.length - shown.length;

  return (
    <nav
      className={className ? `${styles.breadcrumb} ${className}` : styles.breadcrumb}
      aria-label={t('files.breadcrumb.all')}
      /* A root-only trail says exactly what the page title above it already says, so the PHONE hides it —
         that is what the mobile frame draws and what both native apps ship (their breadcrumb row renders
         nothing at the root). A trail with real STEPS stays at every width: there it is the only thing
         naming the folder you are in. Done in CSS off this attribute rather than with a viewport hook, so
         it costs no hydration mismatch and no resize listener. */
      data-root-only={atRoot || undefined}
    >
      {/* At the root the trail IS the root, so it is the current page — the same non-interactive
          `aria-current` crumb the last step of a deeper trail is, never a button back to here. */}
      {atRoot ? (
        <span className={styles.crumbCurrent} aria-current="page">
          {rootLabel ?? t('files.breadcrumb.all')}
        </span>
      ) : (
        <TextButton size="small" className={styles.crumbLink} onClick={() => onNavigate(root)}>
          {rootLabel ?? t('files.breadcrumb.all')}
        </TextButton>
      )}
      {shown.map((segment, i) => {
        const path = [...rootSegments, ...all.slice(0, skipped + i + 1)].reduce((acc, s) => joinPath(acc, s), '');
        const isLast = i === shown.length - 1;
        return (
          <span key={path} className={styles.crumb}>
            <ChevronRight className="dir-flip" size={16} aria-hidden />
            {isLast ? (
              <span className={styles.crumbCurrent} aria-current="page">
                {folderTitle(path, workspaceNames ?? {}, t)}
              </span>
            ) : (
              <TextButton size="small" className={styles.crumbLink} onClick={() => onNavigate(path)}>
                {folderTitle(path, workspaceNames ?? {}, t)}
              </TextButton>
            )}
          </span>
        );
      })}
    </nav>
  );
}
